import { useMutation, useQueryClient } from '@tanstack/react-query';
import { arrayMove } from '@dnd-kit/sortable';
import { bookmarksApi, collectionsApi } from '@/lib/api';
import { bookmarkKeys } from './use-bookmarks';
import { collectionKeys } from './use-collections';
import type { CollectionWithCount, BookmarkWithTags } from '@/types/database';

// Move bookmark to collection with optimistic update
export function useMoveBookmarkToCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bookmarkId,
      collectionId,
    }: {
      bookmarkId: string;
      collectionId: string | null;
    }) => {
      return bookmarksApi.update(bookmarkId, { collection_id: collectionId });
    },
    onMutate: async ({ bookmarkId, collectionId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: bookmarkKeys.all });
      await queryClient.cancelQueries({ queryKey: collectionKeys.all });

      // Snapshot previous values for rollback
      const previousBookmarks = queryClient.getQueriesData<BookmarkWithTags[]>({
        queryKey: bookmarkKeys.lists(),
      });
      const previousCollections = queryClient.getQueryData<CollectionWithCount[]>(
        collectionKeys.list()
      );

      // Optimistically update bookmark's collection_id
      queryClient.setQueriesData<BookmarkWithTags[]>(
        { queryKey: bookmarkKeys.lists() },
        (old) => {
          if (!old) return old;
          return old.map((bookmark) =>
            bookmark.id === bookmarkId
              ? { ...bookmark, collection_id: collectionId }
              : bookmark
          );
        }
      );

      return { previousBookmarks, previousCollections };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousBookmarks) {
        context.previousBookmarks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousCollections) {
        queryClient.setQueryData(collectionKeys.list(), context.previousCollections);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.all });
      queryClient.invalidateQueries({ queryKey: collectionKeys.all });
    },
  });
}

// Reorder collections with optimistic update
export function useReorderCollections() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      collections,
      activeId,
      overId,
    }: {
      collections: CollectionWithCount[];
      activeId: string;
      overId: string;
    }) => {
      const oldIndex = collections.findIndex((c) => c.id === activeId);
      const newIndex = collections.findIndex((c) => c.id === overId);
      const reordered = arrayMove(collections, oldIndex, newIndex);

      // Update order_index for affected collections
      const updates = reordered.map((collection, index) => ({
        id: collection.id,
        order_index: index,
      }));

      // Sequential updates (batch if API supports it later)
      await Promise.all(
        updates.map(({ id, order_index }) =>
          collectionsApi.update(id, { order_index })
        )
      );

      return reordered;
    },
    onMutate: async ({ collections, activeId, overId }) => {
      await queryClient.cancelQueries({ queryKey: collectionKeys.all });

      const previousCollections = queryClient.getQueryData<CollectionWithCount[]>(
        collectionKeys.list()
      );

      // Optimistically reorder
      const oldIndex = collections.findIndex((c) => c.id === activeId);
      const newIndex = collections.findIndex((c) => c.id === overId);
      const reordered = arrayMove(collections, oldIndex, newIndex).map(
        (c, index) => ({ ...c, order_index: index })
      );

      queryClient.setQueryData(collectionKeys.list(), reordered);

      return { previousCollections };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousCollections) {
        queryClient.setQueryData(collectionKeys.list(), context.previousCollections);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.all });
    },
  });
}
